
// Client-side cache management
class CacheManager {
  cachePrefix: string;
  defaultTTL: number;
  longTTL: number;

  constructor() {
    this.cachePrefix = 'bufunfa_';
    this.defaultTTL = 5 * 60 * 1000; // 5 minutos padrão
    this.longTTL = 24 * 60 * 60 * 1000; // 24 horas para dados estáticos
  }

  // Gerar chave de cache consistente
  generateKey(key: string, params: Record<string, any> = {}): string {
    const paramString = Object.keys(params)
      .sort()
      .map(k => `${k}=${params[k]}`)
      .join('&');
    return `${this.cachePrefix}${key}${paramString ? `_${btoa(paramString)}` : ''}`;
  }

  // Set com TTL automático
  set(key: string, data: any, ttl: number = this.defaultTTL): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Cache set failed:', error);
    }
  }

  // Get com verificação de expiração
  get(key: string): any {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const { data, timestamp, ttl } = JSON.parse(cached);
      
      // Verificar se expirou
      if (Date.now() - timestamp > ttl) {
        this.delete(key);
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Cache get failed:', error);
      this.delete(key);
      return null;
    }
  }

  // Delete específico
  delete(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Cache delete failed:', error);
    }
  }

  // Limpar cache expirado
  cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          this.get(key); // Isso remove automaticamente se expirado
        }
      });
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  // Cache para dados de entidades com TTL longo
  cacheEntity(entityName: string, id: string | number, data: any): void {
    const key = this.generateKey(`entity_${entityName}_${id}`);
    this.set(key, data, this.longTTL);
  }

  // Get dados de entidade
  getEntity(entityName: string, id: string | number): any {
    const key = this.generateKey(`entity_${entityName}_${id}`);
    return this.get(key);
  }

  // Cache para queries com parâmetros
  cacheQuery(entityName: string, query: Record<string, any>, data: any): void {
    const key = this.generateKey(`query_${entityName}`, query);
    this.set(key, data, this.defaultTTL);
  }

  // Get query cache
  getQuery(entityName: string, query: Record<string, any>): any {
    const key = this.generateKey(`query_${entityName}`, query);
    return this.get(key);
  }

  // Cache específico para dados do usuário
  cacheUser(userId: string | number, userData: any, ttl: number = this.longTTL): void {
    const key = this.generateKey('user', { id: userId });
    this.set(key, userData, ttl);
  }

  getUser(userId: string | number): any {
    const key = this.generateKey('user', { id: userId });
    return this.get(key);
  }

  // Cache para foto do usuário com TTL longo
  cacheUserPhoto(userId: string | number, photoUrl: string, ttl: number = this.longTTL): void {
    const key = this.generateKey('user_photo', { id: userId });
    this.set(key, photoUrl, ttl);
  }

  getUserPhoto(userId: string | number): string | null {
    const key = this.generateKey('user_photo', { id: userId });
    return this.get(key);
  }

  // Invalidar cache por padrão
  invalidatePattern(pattern: string): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes(pattern)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Cache invalidation failed:', error);
    }
  }
}

// Instância global
export const cacheManager = new CacheManager();

// Auto cleanup periódico
setInterval(() => {
  cacheManager.cleanup();
}, 30 * 60 * 1000); // A cada 30 minutos
